import React from 'react';
import { X, SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import type { ActiveFilterPill } from './types';

interface FilterPillsBarProps {
  pills: ActiveFilterPill[];
  onRemovePill: (filterId: string) => void;
  onClearAll: () => void;
  /** Total active filters app-wide (sidebar badges sum to this). When pills are category-scoped, pass activePills.length. */
  totalActiveAcrossCategories?: number;
  /** Name of the category whose chips are listed (e.g. Trading Style). */
  activeScopeCategoryLabel?: string | null;
  className?: string;
}

export const FilterPillsBar: React.FC<FilterPillsBarProps> = ({
  pills,
  onRemovePill,
  onClearAll,
  totalActiveAcrossCategories,
  activeScopeCategoryLabel,
  className,
}) => {
  const { t } = useTranslation();
  const total =
    totalActiveAcrossCategories !== undefined
      ? totalActiveAcrossCategories
      : pills.length;
  const scopedToCategory =
    Boolean(activeScopeCategoryLabel) &&
    totalActiveAcrossCategories !== undefined;
  const showSplitCount =
    scopedToCategory && pills.length !== totalActiveAcrossCategories;

  if (total === 0 && pills.length === 0) {
    return null;
  }

  const countSummary = showSplitCount
    ? `${pills.length} in ${activeScopeCategoryLabel} · ${total} total active`
    : `${pills.length} active`;

  return (
    <div
      className={cn(
        'flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-0',
        'px-4 md:px-6 py-1.5 md:py-2',
        'bg-surface-container/60 border-b border-border-subtle',
        className
      )}
    >
      {/* Section label - separated from chips so the row doesn’t feel cramped */}
      <div
        className={cn(
          'flex items-center gap-2 shrink-0',
          'sm:pr-3 sm:mr-2 sm:border-r sm:border-border-subtle'
        )}
      >
        <div
          className="flex h-7 w-7 items-center justify-center rounded-lg bg-surface-container-low border border-border-subtle text-accent-primary"
          aria-hidden
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
        </div>
        <span
          className="text-xs text-text-secondary tabular-nums leading-snug min-w-0"
          title={
            showSplitCount
              ? `${pills.length} filter(s) in this category, ${total} across all categories`
              : undefined
          }
        >
          <span className="font-semibold uppercase tracking-wide text-text-muted mr-1.5">
            {t('filters.activeFilters')}:
          </span>
          {countSummary}
        </span>
      </div>

      {/* Pills + clear - scroll horizontally when many */}
      <div className="flex min-w-0 flex-1 items-start gap-2 sm:items-center sm:gap-2.5">
        <div className="flex min-w-0 flex-1 flex-wrap content-start items-center gap-2 pb-0.5">
          {pills.length === 0 ? (
            <span className="text-xs text-text-muted px-1">
              {t('filters.noActiveFiltersInCategory')}
            </span>
          ) : null}
          {pills.map((pill) => {
            const fullText = `${pill.label}: ${pill.displayValue}`;
            return (
              <div
                key={pill.id}
                className={cn(
                  'inline-flex max-w-[min(100%,18rem)] sm:max-w-[20rem] items-stretch overflow-hidden rounded-lg',
                  'border-2 border-accent-primary/50 bg-accent-primary/[0.08]',
                  'shadow-sm ring-1 ring-accent-primary/15',
                  'flex-shrink-0'
                )}
              >
                <span
                  className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left text-xs leading-snug"
                  title={fullText}
                >
                  <span className="truncate text-text-secondary">
                    {pill.label}
                  </span>
                  <span
                    className="shrink-0 rounded-md bg-accent-primary/20 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-accent-primary"
                    aria-hidden
                  >
                    {pill.displayValue}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => onRemovePill(pill.id)}
                  className={cn(
                    'flex shrink-0 items-center justify-center border-l border-accent-primary/25',
                    'bg-accent-primary/5 px-2.5 text-accent-primary',
                    'hover:bg-accent-primary/15 active:bg-accent-primary/25',
                    'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-inset',
                    'min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-[40px] sm:px-3'
                  )}
                  aria-label={`Remove ${pill.label} filter`}
                >
                  <X className="h-4 w-4" strokeWidth={2.25} />
                </button>
              </div>
            );
          })}
        </div>

        {pills.length > 0 ? (
          <button
            type="button"
            onClick={onClearAll}
            className={cn(
              'shrink-0 rounded-lg px-3 py-2 text-xs font-semibold',
              'text-text-muted hover:text-danger hover:bg-danger/10',
              'border border-transparent hover:border-danger/20',
              'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-danger min-h-[44px] sm:min-h-0'
            )}
          >
            {t('filters.clearAll')}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default FilterPillsBar;
